import { useState, useEffect, useCallback } from 'react'
import type Map from 'ol/Map'
import type { MapBrowserEvent } from 'ol'
import { useMapStore } from '../../store/mapStore'
import { ddToMgrs, formatMgrs } from '../../utils/geo/mgrs'
import { formatDDPair, formatDMSPair, ddToDms } from '../../utils/geo/formats'
import { useToast } from '../../hooks/useToast'

interface Props {
  map: Map | null
}

interface CoordDisplay {
  dd: string
  dms: string
  mgrs: string
}

export default function CoordinateBar({ map }: Props) {
  const [coords, setCoords] = useState<CoordDisplay | null>(null)
  const { preferredCoordFormat } = useMapStore()
  const { toast } = useToast()

  const handleMouseMove = useCallback(
    (evt: MapBrowserEvent<PointerEvent>) => {
      // OL EPSG:4326 coordinate: [lon, lat] - GeoJSON order
      const [lon, lat] = evt.coordinate as [number, number]
      if (!isFinite(lat) || !isFinite(lon)) return

      const dd = formatDDPair(lat, lon)
      const dms = formatDMSPair({
        format: 'dms',
        lat: ddToDms(lat, 'lat'),
        lon: ddToDms(lon, 'lon'),
      })
      const mgrsRaw = ddToMgrs(lat, lon)
      const mgrs = mgrsRaw ? formatMgrs(mgrsRaw) : 'N/A'

      setCoords({ dd, dms, mgrs })
    },
    []
  )

  useEffect(() => {
    if (!map) return
    map.on('pointermove', handleMouseMove as (evt: MapBrowserEvent<UIEvent>) => void)
    return () => map.un('pointermove', handleMouseMove as (evt: MapBrowserEvent<UIEvent>) => void)
  }, [map, handleMouseMove])

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text).then(() => {
      toast({ title: `Copied ${label}`, description: text })
    })
  }

  if (!coords) return null

  const items = [
    { label: 'DD', value: coords.dd, format: 'dd' as const },
    { label: 'DMS', value: coords.dms, format: 'dms' as const },
    { label: 'MGRS', value: coords.mgrs, format: 'mgrs' as const },
  ]

  return (
    <div className="absolute bottom-0 left-0 right-0 z-10">
      <div className="flex items-center gap-4 bg-black/60 backdrop-blur-sm border-t border-white/10 px-4 py-1.5 text-xs font-mono">
        {items.map(({ label, value, format }) => (
          <button
            key={label}
            onClick={() => copyToClipboard(value, label)}
            title={`Click to copy ${label}`}
            className={`flex items-center gap-1.5 hover:text-white transition-colors group
              ${format === preferredCoordFormat
                ? 'text-blue-400 font-bold'
                : 'text-white/60'
              }`}
          >
            <span className="text-white/30 group-hover:text-white/50">{label}:</span>
            <span>{value}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
